import { useCallback, useEffect, useState, useRef, useMemo } from "react";
import { DeleteTwoTone, EditTwoTone } from "@ant-design/icons";
import { message, Dropdown, Menu, Popconfirm, Typography } from "antd";
import ListTable from "../../../common/ListTable";
import { useNavigate } from "react-router-dom";
import AppContent from "../../../common/AppContent";
import AppPageHeader from "../../../common/PageHeader";
import ExtraContent from "../CommonComponents/ExtraContent";
import FilterComponent from "../CommonComponents/FilterComponent";
import { getData, deleteData } from "../../../apiServices/common";
import {
  PACKING_OPERATOR_MAPPING_ENDPOINT,
  PACKING_OPERATOR_MAPPING_DELETE_ENDPOINT,
} from "../../../apiServices/API_ENDPOINTS";
import { useSelector } from "react-redux";
import { hasPermission } from "../../../utils/functions";
import {
  PACKING_OPERATOR_MAPPING_CREATE_PERMISSION,
  PACKING_OPERATOR_MAPPING_EDIT_PERMISSION,
  PACKING_OPERATOR_MAPPING_DELETE_PERMISSION,
} from "../../../routes/permissions";
import { useLocation } from "react-router";

const PackingOperatorMappingConfig = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const routerPath = location ? location.pathname : null;

  const [showFilter, setShowFilter] = useState(
    localStorage.getItem("filter_status") && routerPath
      ? JSON.parse(localStorage.getItem("filter_status"))[routerPath]
      : false,
  );
  const [refresh, setRefresh] = useState(0);
  const [tableData, setTableData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [fetchError, setFetchError] = useState(null);
  const [metaData, setMetaData] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);

  const { Title } = Typography;

  const filterData = useSelector(({ filter }) => filter.filterData);
  const prevFilterDataRef = useRef(filterData);
  const currentPageRef = useRef(currentPage);

  useEffect(() => {
    currentPageRef.current = currentPage;
  }, [currentPage]);

  useEffect(() => {
    const filterStatus = localStorage.getItem("filter_status");
    const parsedFilterStatus = filterStatus ? JSON.parse(filterStatus) : {};

    if (showFilter) {
      parsedFilterStatus[routerPath] = true;
      localStorage.setItem("filter_status", JSON.stringify(parsedFilterStatus));
    } else {
      parsedFilterStatus[routerPath] = false;
      localStorage.setItem("filter_status", JSON.stringify(parsedFilterStatus));
    }
  }, [showFilter, routerPath, location]);

  const getAllPackingOperatorMappings = useCallback(async (filterValues, page = null) => {
    const { packing_line_id, operator_id, status } = filterValues || {};
    setLoading(true);
    setFetchError(null);

    let pageToUse;
    if (page !== null) {
      pageToUse = page;
    } else if (filterValues !== null && filterValues !== undefined) {
      pageToUse = 1;
      setCurrentPage(1);
      currentPageRef.current = 1;
    } else {
      pageToUse = currentPageRef.current;
    }

    const query = `${PACKING_OPERATOR_MAPPING_ENDPOINT}`;
    const bodyData = {
      ...(packing_line_id && { packing_line_id: packing_line_id }),
      ...(operator_id && { operator_id: operator_id }),
      ...(status && { status: status }),
      page: pageToUse,
    };

    const response = await getData(query, false, bodyData);
    if (response?.data?.code === 200) {
      setLoading(false);
      const pageFromResponse = response?.data?.current_page || response?.data?.data?.current_page;
      console.log("pageFromResponse", {
        defaultPageSize: response?.data?.per_page || response?.data?.data?.per_page,
        current: pageFromResponse,
        defaultCurrent: pageFromResponse,
        total: response?.data?.total || response?.data?.data?.total,
      });
      setCurrentPage(pageFromResponse);
      currentPageRef.current = pageFromResponse;
      setMetaData({
        defaultPageSize: response?.data?.per_page || response?.data?.data?.per_page,
        current: pageFromResponse,
        defaultCurrent: pageFromResponse,
        total: response?.data?.total || response?.data?.data?.total,
      });
      setTableData(response?.data?.data?.data);
    } else {
      setLoading(false);
      setFetchError("Could not load this list.");
      message.error("Error getting Packer Man mappings data");
    }
  }, []);

  useEffect(() => {
    const isFilterChanged = prevFilterDataRef.current !== filterData && prevFilterDataRef.current !== null;

    if (isFilterChanged) {
      setCurrentPage(1);
      currentPageRef.current = 1;

      prevFilterDataRef.current = filterData;

      getAllPackingOperatorMappings(filterData, 1);
    } else {
      prevFilterDataRef.current = filterData;

      getAllPackingOperatorMappings(filterData, currentPageRef.current);
    }
  }, [refresh, filterData, getAllPackingOperatorMappings]);

  const refetch = () => {
    setRefresh(refresh + 1);
  };

  const handleAdd = () => {
    navigate(`${routerPath}/create`);
  };

  const handleEdit = (record) => {
    navigate(`${routerPath}/edit/${record?.id}`);
  };

  const deleteSelectedMapping = async (id) => {
    setLoading(true);
    const response = await deleteData(`${PACKING_OPERATOR_MAPPING_DELETE_ENDPOINT}/${id}`);

    if (response && response?.code === 200) {
      setLoading(false);
      message.success("Packer Man Mapping Deleted Successfully");
      refetch();
    } else {
      setLoading(false);
      message.error("Something went wrong");
    }
  };

  const changePage = (newPage) => {
    setCurrentPage(newPage);
    getAllPackingOperatorMappings(filterData, newPage);
  };

  const tableColumns = [
    {
      title: "Packing Line",
      dataIndex: ["packing_line"],
      key: "packing_line",
      render: (_, record) => {
        return <div>{record?.packing_line?.name || "-"}</div>;
      },
    },
    {
      title: "Total Hourly Target",
      dataIndex: ["total_hourly_target"],
      key: "total_hourly_target",
      render: (_, record) => {
        return <div>{record?.total_hourly_target || 0}</div>;
      },
    },
    {
      title: "Operators Count",
      dataIndex: ["operators"],
      key: "operators_count",
      render: (_, record) => {
        return <div>{record?.operators?.length || 0}</div>;
      },
    },
    {
      title: "Action",
      key: "action",
      width: "10%",
      render: (_, record) => {
        return (
          <Dropdown.Button
            overlay={
              <Menu
                items={[
                  {
                    label: (
                      <Popconfirm
                        title="Are you sure you want to delete?"
                        onConfirm={() => {
                          deleteSelectedMapping(record?.id);
                        }}
                      >
                        <span>
                          <DeleteTwoTone />
                        </span>
                        <span
                          style={{
                            color: "red",
                            marginLeft: 5,
                          }}
                        >
                          Delete
                        </span>
                      </Popconfirm>
                    ),
                    key: "1",
                    disabled: !hasPermission([PACKING_OPERATOR_MAPPING_DELETE_PERMISSION]),
                  },
                  {
                    label: (
                      <div
                        onClick={() => {
                          handleEdit(record);
                        }}
                        style={{ cursor: "pointer" }}
                      >
                        <span>
                          <EditTwoTone />
                        </span>
                        <span style={{ marginLeft: 5 }}>Edit</span>
                      </div>
                    ),
                    key: "2",
                    disabled: !hasPermission([PACKING_OPERATOR_MAPPING_EDIT_PERMISSION]),
                  },
                ]}
              />
            }
          ></Dropdown.Button>
        );
      },
    },
  ];

  const memoizedCustomFilter = useMemo(
    () => (
      <FilterComponent
        tableData={tableData}
        filterAPI={getAllPackingOperatorMappings}
        filterName="packing_operator_mapping"
        showFilter={showFilter}
        setShowFilter={setShowFilter}
      />
    ),
    [showFilter],
  );

  return (
    <>
      <AppContent
        header={
          <AppPageHeader
            extra={
              <ExtraContent
                refresh={refresh}
                setRefresh={setRefresh}
                handleAddItemOpen={handleAdd}
                disabled={!hasPermission([PACKING_OPERATOR_MAPPING_CREATE_PERMISSION])}
                addButtonText={
                  hasPermission([PACKING_OPERATOR_MAPPING_CREATE_PERMISSION]) ? "Add New" : "No Permission"
                }
                showFilter={showFilter}
                setShowFilter={setShowFilter}
              />
            }
            title={<Title level={5}>Packer Man Mapping Configuration</Title>}
          />
        }
        content={
          <>
            {memoizedCustomFilter}

            <div className="table" style={{ marginTop: 10 }}>
              <ListTable
                loading={loading}
                error={fetchError}
                onRetry={() => getAllPackingOperatorMappings(filterData, currentPageRef.current)}
                emptyText="No records to show yet."
                dataSource={tableData}
                columns={tableColumns}
                pagination={{
                  showSizeChanger: false,
                  defaultPageSize: metaData?.defaultPageSize,
                  current: metaData?.current,
                  onChange: (newPage) => {
                    changePage(newPage);
                  },
                  defaultCurrent: metaData?.defaultCurrent,
                  total: metaData?.total,
                }}
              />
            </div>
          </>
        }
      />
    </>
  );
};

export default PackingOperatorMappingConfig;
